/**
 * MetaTags Component
 *
 * Reusable SEO meta tags component using react-helmet-async.
 * Provides comprehensive SEO optimization including:
 * - Page titles and descriptions
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Canonical URLs
 * - Structured data (JSON-LD schema)
 *
 * @module components/SEO/MetaTags
 */

import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

/**
 * MetaTags component for SEO optimization
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title (will be appended with site name)
 * @param {string} props.description - Page description for search results
 * @param {string} [props.keywords] - Comma-separated keywords
 * @param {string} [props.ogTitle] - Open Graph title (defaults to title)
 * @param {string} [props.ogDescription] - Open Graph description (defaults to description)
 * @param {string} [props.ogImage] - Open Graph image URL
 * @param {string} [props.ogUrl] - Open Graph URL (should match canonical URL)
 * @param {string} [props.ogType='website'] - Open Graph type
 * @param {string} [props.twitterTitle] - Twitter Card title (defaults to ogTitle/title)
 * @param {string} [props.twitterDescription] - Twitter Card description (defaults to ogDescription/description)
 * @param {string} [props.twitterImage] - Twitter Card image URL (defaults to ogImage)
 * @param {string} [props.twitterCard='summary_large_image'] - Twitter Card type
 * @param {string} props.canonicalUrl - Canonical URL for this page
 * @param {Object} [props.schema] - JSON-LD structured data object
 * @param {boolean} [props.noindex=false] - Prevent search engine indexing
 * @param {boolean} [props.nofollow=false] - Prevent search engines from following links
 * @returns {JSX.Element} Helmet component with meta tags
 */
const MetaTags = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCard = 'summary_large_image',
  canonicalUrl,
  schema,
  noindex = false,
  nofollow = false
}) => {
  // Construct full title with site branding
  // If title already starts with "CueSesh", use it as-is (for homepage)
  // Otherwise append site branding (for other pages)
  const fullTitle = title
    ? title.startsWith('CueSesh')
      ? title
      : `${title} | CueSesh`
    : 'CueSesh - Listen like you live.';

  // Fallback values for social sharing
  const socialTitle = twitterTitle || ogTitle || title;
  const socialDescription = twitterDescription || ogDescription || description;
  const ogImageUrl = ogImage || twitterImage || 'https://cuesesh.com/og-default.png';
  const twitterImageUrl = twitterImage || ogImage || 'https://cuesesh.com/og-default.png';

  // Construct robots meta tag
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  const robotsTag = robotsContent.length > 0 ? robotsContent.join(', ') : 'index, follow';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsTag} />

      {/* Open Graph Tags (Facebook, LinkedIn, etc.) */}
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:url" content={ogUrl || canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="CueSesh" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={twitterImageUrl} />
      <meta name="twitter:site" content="@cuesesh" />
      <meta name="twitter:creator" content="@cuesesh" />

      {/* Additional Meta Tags */}
      <meta name="author" content="CueSesh" />
      <meta name="theme-color" content="#15101A" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Structured Data (JSON-LD) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

MetaTags.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string.isRequired,
  keywords: PropTypes.string,
  ogTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  ogImage: PropTypes.string,
  ogUrl: PropTypes.string,
  ogType: PropTypes.string,
  twitterTitle: PropTypes.string,
  twitterDescription: PropTypes.string,
  twitterImage: PropTypes.string,
  twitterCard: PropTypes.string,
  canonicalUrl: PropTypes.string.isRequired,
  schema: PropTypes.object,
  noindex: PropTypes.bool,
  nofollow: PropTypes.bool
};

export default MetaTags;
